<template>
  <!-- No exam scheduled -->
  <div v-if="examState === 'none'" class="text-center py-8">
    <img :src="quizIcon" class="w-16 h-16 mx-auto mb-4" />
    <h2 class="text-2xl font-bold mb-4">Examen</h2>
    <p class="text-gray-600">Examenul nu a fost programat inca.</p>
  </div>

  <!-- Exam scheduled but not started -->
  <div v-else-if="examState === 'unstarted'" class="text-center py-8">
    <img :src="quizIcon" class="w-16 h-16 mx-auto mb-4" />
    <h2 class="text-2xl font-bold mb-4">Examen programat</h2>
    <p v-if="subject.exam_state?.total_questions" class="text-gray-600 mb-4">
      Numar de intrebari: {{ subject.exam_state.total_questions }}
    </p>
    <!-- eslint-disable vue/no-v-html -->
    <div
      v-if="subject.description"
      class="prose max-w-none mb-4 text-left"
      v-html="sanitizedDescription"
    ></div>
    <!-- eslint-enable vue/no-v-html -->
    <button
      class="bg-secondary-color hover:bg-blue-950 py-3 px-8 rounded-lg text-white font-semibold"
      @click="startExam"
    >
      Incepe examenul
    </button>
  </div>

  <!-- Rejoin exam -->
  <div
    v-else-if="examState === 'in_progress' && examNeedsRejoin"
    class="text-center py-8"
  >
    <img :src="quizIcon" class="w-16 h-16 mx-auto mb-4" />
    <h2 class="text-2xl font-bold mb-4">Examen in desfasurare</h2>
    <div
      v-if="
        examRejoinFromOtherDevice ||
        (subject.exam_state?.has_active_session &&
          !subject.exam_state?.is_current_device)
      "
      class="mb-6 p-4 bg-yellow-50 border border-yellow-300 rounded-lg text-yellow-800 text-sm max-w-md mx-auto text-left"
    >
      <i class="pi pi-exclamation-triangle mr-2"></i>
      Examenul este deja activ pe un alt dispozitiv. Daca te reconectezi,
      sesiunea de pe celalalt dispozitiv va fi inchisa.
    </div>
    <button
      class="bg-secondary-color hover:bg-blue-950 py-3 px-8 rounded-lg text-white font-semibold"
      @click="rejoinExam"
    >
      Rejoin examen
    </button>
  </div>

  <!-- Exam in progress -->
  <div
    v-else-if="examState === 'in_progress' && examQuestions.length > 0"
    class="py-8"
  >
    <!-- Countdown timer -->
    <div class="mb-4 flex items-center justify-between">
      <div class="text-sm text-gray-600">
        Parcurse: {{ examSeenCount }}/{{ examQuestions.length }}
        <span v-if="examRevisitCount > 0">
          | De revizitat: {{ examRevisitCount }}
        </span>
      </div>
      <div class="flex items-center gap-2">
        <transition
          enter-active-class="transition-all duration-500 ease-out"
          enter-from-class="opacity-0 -translate-y-2"
          enter-to-class="opacity-100 translate-y-0"
          leave-active-class="transition-all duration-700 ease-in"
          leave-from-class="opacity-100 translate-y-0"
          leave-to-class="opacity-0 translate-y-2"
        >
          <span
            v-if="examTimeAddedMinutes"
            class="text-sm font-bold text-green-600 bg-green-100 px-2 py-1 rounded"
            >+{{ examTimeAddedMinutes }} min</span
          >
        </transition>
        <div
          class="text-lg font-bold px-3 py-1 rounded"
          :class="
            examCountdownMinutes < 2
              ? 'bg-red-100 text-red-700'
              : 'bg-blue-100 text-blue-700'
          "
        >
          <i class="pi pi-clock mr-1"></i>
          {{ examCountdownDisplay }}
        </div>
      </div>
    </div>

    <h3 class="text-xl font-semibold mb-6">
      {{ currentExamQuestion.text }}
    </h3>

    <div class="space-y-3">
      <button
        v-for="(answer, idx) in currentExamQuestion.answers"
        :key="idx"
        class="w-full text-left p-4 rounded-lg border-2 transition-all hover:border-secondary-color hover:bg-blue-50 flex items-center gap-3"
        :class="{
          'border-secondary-color bg-blue-50': examSelectedAnswer === answer,
          'border-gray-300': examSelectedAnswer !== answer,
        }"
        @click="examSelectedAnswer = answer"
      >
        <div
          class="w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0"
          :class="{
            'border-secondary-color bg-secondary-color':
              examSelectedAnswer === answer,
            'border-gray-400': examSelectedAnswer !== answer,
          }"
        >
          <i
            v-if="examSelectedAnswer === answer"
            class="pi pi-check text-white text-xs"
          ></i>
        </div>
        <span>{{ answer }}</span>
      </button>
    </div>

    <div class="mt-6 flex justify-between">
      <button
        class="border-2 border-yellow-500 text-yellow-700 hover:bg-yellow-50 py-3 px-6 rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
        :disabled="!examSelectedAnswer"
        @click="examAnswerAndRevisit"
      >
        Revino mai tarziu
      </button>
      <button
        :disabled="!examSelectedAnswer"
        class="bg-secondary-color hover:bg-blue-950 py-3 px-8 rounded-lg text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
        @click="examFinalAnswer"
      >
        Raspuns final
      </button>
    </div>
  </div>

  <!-- Exam loading -->
  <div
    v-else-if="examState === 'in_progress' && examQuestions.length === 0"
    class="text-center py-8"
  >
    <i class="pi pi-spin pi-spinner text-secondary-color text-6xl"></i>
    <p class="text-gray-600 mt-4">Se incarca examenul...</p>
  </div>

  <!-- Exam finished -->
  <div v-else-if="examState === 'finished'" class="text-center py-8">
    <div class="mb-6 flex justify-center">
      <img
        :src="examPassed ? passIcon : failIcon"
        :alt="examPassed ? 'Pass' : 'Fail'"
        class="w-32 h-32 object-contain"
      />
    </div>
    <div
      class="text-4xl font-bold mb-2"
      :style="{ color: examPassed ? '#92D050' : '#D1805D' }"
    >
      {{ examCorrectCount }} / {{ examTotalQuestions }} ({{
        parseFloat(examResult).toFixed(1)
      }}%)
    </div>
    <h2 class="text-2xl font-bold mb-4">
      {{ examPassed ? 'Promovat!' : 'Nepromovat!' }}
    </h2>
    <p class="text-gray-600">
      {{
        examPassed
          ? 'Felicitari!'
          : 'Procedura de reluare a testarii se va discuta cu coach-ul alocat!'
      }}
    </p>
  </div>
</template>

<script>
import axios from 'axios';
import sanitizeHtml from 'sanitize-html';
import { quizResultPercentage, quizResultPassed } from '@/utils/formatters';

export default {
  props: {
    subject: {
      type: Object,
      required: true,
    },
    passIcon: {
      type: String,
      required: true,
    },
    failIcon: {
      type: String,
      required: true,
    },
    quizIcon: {
      type: String,
      required: true,
    },
  },
  emits: ['update:isInProgress'],
  data() {
    return {
      examQuestions: [],
      examCurrentIndex: 0,
      examSelectedAnswer: null,
      examEndTime: null,
      examCountdownSeconds: 0,
      examCountdownInterval: null,
      examQuizResultId: null,
      examPhase: 'first_pass',
      examFinalResult: null,
      examTimeAddedMinutes: null,
      examSessionToken: null,
      examNeedsRejoin: false,
      examRejoinFromOtherDevice: false,
      examCheckingExpiry: false,
    };
  },
  computed: {
    sanitizedDescription() {
      return sanitizeHtml(this.subject.description || '', {
        allowedTags: sanitizeHtml.defaults.allowedTags.concat(['img']),
        allowedAttributes: {
          ...sanitizeHtml.defaults.allowedAttributes,
          '*': ['class', 'style'],
        },
      });
    },
    examState() {
      if (this.examFinalResult !== null) return 'finished';
      if (this.examCheckingExpiry) return 'in_progress';
      if (this.examNeedsRejoin) return 'in_progress';
      if (this.examQuestions.length > 0 && this.examEndTime) {
        const now = Date.now();
        const end = new Date(this.examEndTime).getTime();
        if (now < end) return 'in_progress';
        return 'finished';
      }
      return this.subject.exam_state?.status || 'none';
    },
    isInProgress() {
      return this.examState === 'in_progress';
    },
    currentExamQuestion() {
      return this.examQuestions[this.examCurrentIndex] || {};
    },
    examUnseenCount() {
      return this.examQuestions.filter((q) => !q.answer).length;
    },
    examSeenCount() {
      return this.examQuestions.length - this.examUnseenCount;
    },
    examRevisitCount() {
      return this.examQuestions.filter((q) => q.revisit).length;
    },
    examCountdownMinutes() {
      return Math.floor(this.examCountdownSeconds / 60);
    },
    examCountdownDisplay() {
      const mins = Math.floor(this.examCountdownSeconds / 60);
      const secs = this.examCountdownSeconds % 60;
      return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    },
    examPassed() {
      if (this.examFinalResult !== null)
        return quizResultPassed(
          this.examFinalResult.correct_count,
          this.examFinalResult.total_questions,
        );
      return quizResultPassed(this.examCorrectCount, this.examTotalQuestions);
    },
    examResult() {
      return quizResultPercentage(
        this.examCorrectCount,
        this.examTotalQuestions,
      );
    },
    examCorrectCount() {
      return (
        this.examFinalResult?.correct_count ??
        this.subject.exam_state?.correct_count ??
        0
      );
    },
    examTotalQuestions() {
      return (
        this.examFinalResult?.total_questions ??
        this.subject.exam_state?.total_questions ??
        0
      );
    },
  },
  watch: {
    isInProgress(val) {
      this.$emit('update:isInProgress', val);
    },
  },
  mounted() {
    if (this.subject.exam_state?.status === 'in_progress') {
      this.examNeedsRejoin = true;
    }
    this.$emit('update:isInProgress', this.isInProgress);
  },
  beforeUnmount() {
    this.stopExamCountdown();
  },
  methods: {
    async startExam() {
      try {
        const response = await axios.post(
          this.$routes.start_exam_subject(this.subject.id),
        );
        if (response.data.already_started) {
          this.examNeedsRejoin = true;
          this.examRejoinFromOtherDevice = true;
          return;
        }
        this.examQuizResultId = response.data.quiz_result_id;
        this.examSessionToken = response.data.device_token;
        this.examEndTime = response.data.end_time;
        this.examQuestions = response.data.questions.map((q) => ({
          ...q,
          _revisitDone: false,
        }));
        this.examCurrentIndex = 0;
        this.examSelectedAnswer = null;
        this.examPhase = 'first_pass';
        this.startExamCountdown();
      } catch (error) {
        console.error('Error starting exam:', error);
        alert(error.response?.data?.error || 'Eroare la inceperea examenului.');
      }
    },
    startExamCountdown() {
      this.updateCountdown();
      this.examCountdownInterval = setInterval(() => {
        this.updateCountdown();
        if (this.examCountdownSeconds <= 0) {
          this.onExamTimeExpired();
        }
      }, 1000);
    },
    stopExamCountdown() {
      if (this.examCountdownInterval) {
        clearInterval(this.examCountdownInterval);
        this.examCountdownInterval = null;
      }
    },
    updateCountdown() {
      if (!this.examEndTime) {
        this.examCountdownSeconds = 0;
        return;
      }
      const end = new Date(this.examEndTime).getTime();
      const now = Date.now();
      this.examCountdownSeconds = Math.max(0, Math.floor((end - now) / 1000));
    },
    async onExamTimeExpired() {
      this.stopExamCountdown();
      this.examCheckingExpiry = true;
      try {
        const response = await axios.post(
          this.$routes.rejoin_exam_subject(this.subject.id),
        );
        const newEndTime = response.data.end_time;
        if (
          !response.data.concluded &&
          newEndTime &&
          new Date(newEndTime).getTime() > Date.now()
        ) {
          // Admin added time — resume the exam
          const added = Math.round(
            (new Date(newEndTime) - new Date(this.examEndTime)) / 60000,
          );
          if (added > 0) {
            this.examTimeAddedMinutes = added;
            setTimeout(() => {
              this.examTimeAddedMinutes = null;
            }, 4000);
          }
          this.examEndTime = newEndTime;
          this.examSessionToken = response.data.device_token;
          this.examCheckingExpiry = false;
          this.startExamCountdown();
        } else if (response.data.concluded) {
          this.examFinalResult = {
            correct_count: response.data.correct_count,
            total_questions: response.data.total_questions,
          };
          this.examCheckingExpiry = false;
        } else if (response.data.time_expired) {
          // Server confirmed time expired and returns counts directly
          this.examFinalResult = {
            correct_count: response.data.correct_count ?? 0,
            total_questions: response.data.total_questions ?? 0,
          };
          this.examCheckingExpiry = false;
        } else {
          // Server still sees exam as active — reload to let server finalize.
          // Keep examCheckingExpiry = true to avoid a flash of 0 score.
          this.$inertia.reload({
            onSuccess: () => {
              this.examFinalResult = {
                correct_count: this.subject.exam_state?.correct_count,
                total_questions: this.subject.exam_state?.total_questions,
              };
              this.examCheckingExpiry = false;
            },
            onError: () => {
              this.examCheckingExpiry = false;
            },
          });
        }
      } catch (error) {
        console.error('Error verifying exam expiry:', error);
        // Keep examCheckingExpiry = true and reload; use onSuccess to set result.
        this.$inertia.reload({
          onSuccess: () => {
            const state = this.subject.exam_state;
            if (state?.status === 'finished' && state?.correct_count != null) {
              this.examFinalResult = {
                correct_count: state.correct_count,
                total_questions: state.total_questions,
              };
            }
            this.examCheckingExpiry = false;
          },
          onError: () => {
            this.examCheckingExpiry = false;
          },
        });
      }
    },
    async examNextQuestion(revisit = false) {
      if (!this.examSelectedAnswer) return;

      const current = this.examQuestions[this.examCurrentIndex];

      if (this.examCountdownSeconds <= 0) {
        this.onExamTimeExpired();
        return;
      }

      try {
        const response = await axios.post(
          this.$routes.answer_exam_subject(this.subject.id),
          {
            exam_answer_id: current.exam_answer_id,
            answer: this.examSelectedAnswer,
            revisit: revisit,
            device_token: this.examSessionToken,
          },
        );

        if (response.data.session_terminated) {
          this.examNeedsRejoin = true;
          this.examRejoinFromOtherDevice = true;
          this.examSessionToken = null;
          this.examQuestions = [];
          this.stopExamCountdown();
          return;
        }

        if (response.data.time_expired) {
          this.onExamTimeExpired();
          return;
        }

        if (
          response.data.end_time &&
          response.data.end_time !== this.examEndTime
        ) {
          const added = Math.round(
            (new Date(response.data.end_time) - new Date(this.examEndTime)) /
              60000,
          );
          if (added > 0) {
            this.examTimeAddedMinutes = added;
            setTimeout(() => {
              this.examTimeAddedMinutes = null;
            }, 4000);
          }
          this.examEndTime = response.data.end_time;
        }

        if (response.data.completed) {
          this.stopExamCountdown();
          this.examFinalResult = {
            correct_count: response.data.correct_count,
            total_questions: response.data.total_questions,
          };
          return;
        }

        current.answer = this.examSelectedAnswer;
        current.revisit = revisit;
        if (this.examPhase === 'revisit' && !revisit) {
          current._revisitDone = true;
        }
      } catch (error) {
        console.error('Error saving exam answer:', error);
        alert('Eroare la salvarea raspunsului.');
        return;
      }

      this.examSelectedAnswer = null;

      if (this.examPhase === 'first_pass') {
        const nextIdx = this.examQuestions.findIndex(
          (q, idx) => idx > this.examCurrentIndex && !q.answer,
        );
        if (nextIdx >= 0) {
          this.examCurrentIndex = nextIdx;
          this.examSelectedAnswer =
            this.examQuestions[this.examCurrentIndex]?.answer || null;
        } else {
          this.enterRevisitPhase();
        }
      } else {
        this.advanceRevisitPhase();
      }
    },
    examAnswerAndRevisit() {
      if (!this.examSelectedAnswer) return;
      this.examQuestions[this.examCurrentIndex].revisit = true;
      this.examNextQuestion(true);
    },
    examFinalAnswer() {
      if (!this.examSelectedAnswer) return;
      this.examQuestions[this.examCurrentIndex].revisit = false;
      this.examNextQuestion(false);
    },
    async rejoinExam() {
      try {
        const response = await axios.post(
          this.$routes.rejoin_exam_subject(this.subject.id),
        );
        const { device_token, quiz_result_id, end_time, questions, concluded } =
          response.data;
        if (concluded) {
          this.$inertia.reload();
          return;
        }
        this.examSessionToken = device_token;
        this.examNeedsRejoin = false;
        this.examQuizResultId = quiz_result_id;
        this.examEndTime = end_time;
        this.examQuestions = questions.map((q) => ({
          ...q,
          _revisitDone: false,
        }));
        const firstUnanswered = this.examQuestions.findIndex((q) => !q.answer);
        if (firstUnanswered >= 0) {
          this.examCurrentIndex = firstUnanswered;
          this.examPhase = 'first_pass';
        } else {
          const firstRevisit = this.examQuestions.findIndex((q) => q.revisit);
          if (firstRevisit >= 0) {
            this.examCurrentIndex = firstRevisit;
            this.examPhase = 'revisit';
          } else {
            this.$inertia.reload();
            return;
          }
        }
        this.examSelectedAnswer =
          this.examQuestions[this.examCurrentIndex]?.answer || null;
        this.startExamCountdown();
      } catch (error) {
        console.error('Error rejoining exam:', error);
        alert(
          error.response?.data?.error || 'Eroare la reconectarea la examen.',
        );
      }
    },
    enterRevisitPhase() {
      const revisitIdx = this.examQuestions.findIndex(
        (q) => q.revisit && !q._revisitDone,
      );
      if (revisitIdx >= 0) {
        this.examPhase = 'revisit';
        this.examCurrentIndex = revisitIdx;
        this.examSelectedAnswer =
          this.examQuestions[this.examCurrentIndex]?.answer || null;
      } else {
        this.stopExamCountdown();
      }
    },
    advanceRevisitPhase() {
      const currentIdx = this.examCurrentIndex;
      let nextIdx = this.examQuestions.findIndex(
        (q, idx) => idx > currentIdx && q.revisit && !q._revisitDone,
      );
      if (nextIdx < 0) {
        nextIdx = this.examQuestions.findIndex(
          (q, idx) => idx !== currentIdx && q.revisit && !q._revisitDone,
        );
      }
      if (nextIdx < 0) {
        const currentQ = this.examQuestions[currentIdx];
        if (currentQ?.revisit && !currentQ?._revisitDone) {
          nextIdx = currentIdx;
        }
      }
      if (nextIdx >= 0) {
        this.examCurrentIndex = nextIdx;
        this.examSelectedAnswer = this.examQuestions[nextIdx]?.answer || null;
      } else {
        this.stopExamCountdown();
      }
    },
  },
};
</script>
